import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Trash, Copy } from "@phosphor-icons/react";

const ROLE_LABELS = { mother: "Mother", father: "Father", grandparent: "Grandparent", sibling: "Sibling", family: "Family" };
const PERM_LABELS = { view: "View only", contribute: "Contribute", admin: "Admin" };

export default function FamilyCircle() {
  const [data, setData] = useState({ members: [], pending_invites: [] });
  const [adding, setAdding] = useState(false);
  const [inv, setInv] = useState({ name: "", email: "", role: "father", permission: "contribute" });
  const [latestCode, setLatestCode] = useState(null);
  const [joinCode, setJoinCode] = useState("");

  const load = async () => { const r = await api.get("/family/circle"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/family/invite", inv);
      setLatestCode(r.data.code);
      setInv({ name: "", email: "", role: "father", permission: "contribute" });
      setAdding(false);
      load();
      toast.success("Invitation created. Share the code with your family member.");
    } catch { toast.error("Could not create invitation."); }
  };

  const join = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/family/accept-invite?code=${encodeURIComponent(joinCode)}`);
      toast.success("Joined the family.");
      setJoinCode("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not join."); }
  };

  const cancel = async (id) => { await api.delete(`/family/invite/${id}`); load(); };
  const removeMember = async (id) => { try { await api.delete(`/family/member/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Cannot remove."); } };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Code copied."); };

  return (
    <div className="space-y-8" data-testid="family-circle-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-saffron mb-2">Your circle</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Family circle</h1>
          <p className="text-ink-700 mt-2">Father, grandparents, siblings — invite the ones who matter.</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="btn-primary" data-testid="invite-btn"><Plus weight="thin" size={18} /> Invite</button>
      </header>

      {latestCode && (
        <div className="vansha-card p-6 flex items-center justify-between gap-4 bg-forest text-white" data-testid="latest-code">
          <div>
            <p className="overline" style={{ color: "#D9C9B4" }}>Invite code</p>
            <p className="font-mono text-3xl">{latestCode}</p>
            <p className="text-sm opacity-80 mt-1">Ask them to register and use this code on the family page.</p>
          </div>
          <button onClick={() => copy(latestCode)} className="bg-saffron text-forest px-4 py-2 rounded-full" data-testid="copy-code"><Copy weight="thin" size={16} /> Copy</button>
        </div>
      )}

      {adding && (
        <form onSubmit={submit} className="vansha-card p-6 grid sm:grid-cols-2 gap-5" data-testid="invite-form">
          <div><label className="overline mb-2 block">Name</label><input required value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className="input-line" data-testid="inv-name" /></div>
          <div><label className="overline mb-2 block">Email (optional)</label><input value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} className="input-line" data-testid="inv-email" /></div>
          <div><label className="overline mb-2 block">Role</label>
            <select value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })} className="input-line bg-transparent" data-testid="inv-role">
              {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><label className="overline mb-2 block">Permission</label>
            <select value={inv.permission} onChange={(e) => setInv({ ...inv, permission: e.target.value })} className="input-line bg-transparent" data-testid="inv-perm">
              {Object.entries(PERM_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 flex gap-3 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="inv-submit">Create invite</button>
          </div>
        </form>
      )}

      <section data-testid="members-section">
        <h2 className="font-serif text-2xl mb-4">Members ({data.members.length})</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.members.map((m) => (
            <div key={m.id} className="vansha-card p-5" data-testid={`member-${m.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-serif text-xl">{m.name}</h3>
                  <p className="text-sm text-ink-500 mt-1 capitalize">{ROLE_LABELS[m.role] || m.role} · {PERM_LABELS[m.permission]}</p>
                  {m.email && <p className="text-xs text-ink-500 mt-1">{m.email}</p>}
                </div>
                <Users weight="thin" size={20} className="text-terracotta" />
              </div>
              {m.permission !== "admin" && <button onClick={() => removeMember(m.id)} className="text-xs text-ink-500 hover:text-terracotta mt-3" data-testid={`remove-${m.id}`}>Remove</button>}
            </div>
          ))}
        </div>
      </section>

      {data.pending_invites?.length > 0 && (
        <section data-testid="pending-section">
          <h2 className="font-serif text-2xl mb-4">Pending invitations</h2>
          <div className="space-y-3">
            {data.pending_invites.map((i) => (
              <div key={i.id} className="vansha-card p-5 flex items-center justify-between" data-testid={`invite-${i.id}`}>
                <div>
                  <p className="font-medium">{i.name}</p>
                  <p className="text-sm text-ink-500">{ROLE_LABELS[i.role] || i.role}</p>
                </div>
                <button onClick={() => cancel(i.id)} className="btn-ghost p-2"><Trash weight="thin" size={16} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="vansha-card p-6" data-testid="join-section">
        <p className="overline text-saffron mb-2">Have an invite code?</p>
        <form onSubmit={join} className="flex gap-3">
          <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} className="input-line flex-1 font-mono" placeholder="ABCD12345" data-testid="join-code-input" />
          <button className="btn-secondary" data-testid="join-submit">Join family</button>
        </form>
      </section>
    </div>
  );
}
